import type {
  BusinessRules,
  DefaultValues,
  FabricBusinessRulesItem,
  HiddenOptionsAndValues,
  Option,
  OptionConfigurationType,
  OptionsBusinessRulesItem,
  OptionsSelection,
  StylePreset,
  typeOptionValue,
} from './interfaces';
import type { ConfigurationTypes, ProductType } from './productDefinition';
import {
  getExtraConfgurationByBaseConfiguration,
  getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId,
  Products,
} from './productDefinition';
import {
  getProductDefaultHiddenItemsState,
  initialiseConfigurationBasedOptionsSelection,
  isExtraConfigurationInProduct,
  logConsole,
} from './utils';

export const isOptionValueHiddenByRule = (
  optionValueId: number,
  hiddenOptionsAndValues?: HiddenOptionsAndValues,
  affectedConfigurationType?: ConfigurationTypes,
) => {
  if (!affectedConfigurationType || !hiddenOptionsAndValues?.optionValuesPerConfiguration[affectedConfigurationType])
    return false;

  return hiddenOptionsAndValues.optionValuesPerConfiguration[affectedConfigurationType]?.includes(optionValueId);
};

export const isOptionHiddenByRule = (
  optionId: number,
  hiddenOptionsAndValues?: HiddenOptionsAndValues,
  affectedConfigurationType?: ConfigurationTypes,
) => {
  if (!affectedConfigurationType || !hiddenOptionsAndValues?.optionsPerConfiguration[affectedConfigurationType])
    return false;

  return hiddenOptionsAndValues.optionsPerConfiguration[affectedConfigurationType]?.includes(optionId);
};

// This one might need refactoring and contract change for fabric business rules, because now it applies to all configuration types
const triggerFabricSelectionBasedHideRules = (
  item: FabricBusinessRulesItem,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  affectedConfigurationType: ConfigurationTypes,
  isLogEnabled: boolean,
) => {
  // Loop hide rules for selection on optionValues
  item.hide?.optionValues?.forEach(optionValueId => {
    if (!isOptionValueHiddenByRule(optionValueId, hiddenOptionsAndValues, affectedConfigurationType)) {
      logConsole(
        'triggerFabricSelectionBasedHideRules for optionValue',
        { optionValueId, hiddenOptionsAndValues, item },
        isLogEnabled,
      );
      hiddenOptionsAndValues.optionValuesPerConfiguration[affectedConfigurationType].push(optionValueId);
    }
  });

  // Loop hide rules for selection on options if exists
  item.hide?.options?.forEach(optionId => {
    if (!isOptionHiddenByRule(optionId, hiddenOptionsAndValues, affectedConfigurationType)) {
      logConsole(
        'triggerFabricSelectionBasedHideRules for option',
        { optionId, hiddenOptionsAndValues, item },
        isLogEnabled,
      );
      hiddenOptionsAndValues.optionsPerConfiguration[affectedConfigurationType].push(optionId);
    }
  });

  // Loop hide rules for selection on monogram color if exists
  item.hide?.monogramColors?.forEach(monogramColorId => {
    if (hiddenOptionsAndValues.monogramColors.indexOf(monogramColorId) < 0) {
      logConsole(
        'triggerFabricSelectionBasedHideRules for monogram color',
        { monogramColorId, hiddenOptionsAndValues, item },
        isLogEnabled,
      );
      hiddenOptionsAndValues.monogramColors.push(monogramColorId);
    }
  });

  // Loop hide rules for selection on monogram position if exists
  item.hide?.monogramPositions?.forEach(monogramPositionId => {
    if (hiddenOptionsAndValues.monogramPositions.indexOf(monogramPositionId) < 0) {
      logConsole(
        'triggerFabricSelectionBasedHideRules for monogram position',
        { monogramPositionId, hiddenOptionsAndValues, item },
        isLogEnabled,
      );
      hiddenOptionsAndValues.monogramPositions.push(monogramPositionId);
    }
  });
};

const triggerOptionSelectionBasedHideRules = (
  item: OptionsBusinessRulesItem,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  affectedConfigurationType: ConfigurationTypes,
  isLogEnabled: boolean,
) => {
  // Loop hide rules for selection on optionValues
  if (item.hide?.optionValues) {
    Object.entries(item.hide?.optionValues).forEach(([applyToConfigurationType, optionValueIds]) => {
      optionValueIds.forEach(optionValueId => {
        if (
          !isOptionValueHiddenByRule(
            optionValueId,
            hiddenOptionsAndValues,
            applyToConfigurationType as ConfigurationTypes,
          )
        ) {
          logConsole(
            'triggerOptionSelectionBasedHideRules for optionValue',
            { optionValueId, hiddenOptionsAndValues, item },
            isLogEnabled,
          );
          hiddenOptionsAndValues.optionValuesPerConfiguration[applyToConfigurationType as ConfigurationTypes] = [
            ...hiddenOptionsAndValues.optionValuesPerConfiguration[applyToConfigurationType as ConfigurationTypes],
            optionValueId,
          ];
        }
      });
    });
  }

  // Loop hide rules for selection on options if exists
  item.hide?.options?.forEach(optionId => {
    if (!isOptionHiddenByRule(optionId, hiddenOptionsAndValues, affectedConfigurationType)) {
      logConsole(
        'triggerOptionSelectionBasedHideRules for option',
        { optionId, hiddenOptionsAndValues, item },
        isLogEnabled,
      );
      hiddenOptionsAndValues.optionsPerConfiguration[affectedConfigurationType] = [
        ...hiddenOptionsAndValues.optionsPerConfiguration[affectedConfigurationType],
        optionId,
      ];
    }
  });

  // Loop hide rules for selection on monogram color if exists
  item.hide?.monogramColors?.forEach(monogramColorId => {
    if (hiddenOptionsAndValues.monogramColors.indexOf(monogramColorId) < 0) {
      logConsole(
        'triggerOptionSelectionBasedHideRules for monogram color',
        { monogramColorId, hiddenOptionsAndValues, item },
        isLogEnabled,
      );
      hiddenOptionsAndValues.monogramColors.push(monogramColorId);
    }
  });

  // Loop hide rules for selection on monogram position if exists
  item.hide?.monogramPositions?.forEach(monogramPositionId => {
    if (hiddenOptionsAndValues.monogramPositions.indexOf(monogramPositionId) < 0) {
      logConsole(
        'triggerOptionSelectionBasedHideRules for monogram position',
        { monogramPositionId, hiddenOptionsAndValues, item },
        isLogEnabled,
      );
      hiddenOptionsAndValues.monogramPositions.push(monogramPositionId);
    }
  });
};

const unhidePreviouslyHiddenMonogramValuesBySelection = (
  prevItem: FabricBusinessRulesItem | OptionsBusinessRulesItem,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  isLogEnabled: boolean,
  tmpCheckHiddenOptionsAndValues?: HiddenOptionsAndValues,
) => {
  // Loop hide rules for option selection on monogram color if exists
  prevItem.hide?.monogramColors?.forEach((hiddenMonogramColorId: number) => {
    if (
      !tmpCheckHiddenOptionsAndValues ||
      tmpCheckHiddenOptionsAndValues.monogramColors.indexOf(hiddenMonogramColorId) < 0
    ) {
      const valueIndex = hiddenOptionsAndValues.monogramColors.indexOf(hiddenMonogramColorId);
      if (valueIndex >= 0) {
        logConsole(
          'checkHiddenOptionsValuesToRemoveOptionItem for monogram color',
          { valueIndex, hiddenMonogramColorId, hiddenOptionsAndValues },
          isLogEnabled,
        );
        hiddenOptionsAndValues.monogramColors.splice(valueIndex, 1);
      }
    }
  });

  // Loop hide rules for option selection on monogram position if exists
  prevItem.hide?.monogramPositions?.forEach((hiddenMonogramPositionId: number) => {
    if (
      !tmpCheckHiddenOptionsAndValues ||
      tmpCheckHiddenOptionsAndValues.monogramPositions.indexOf(hiddenMonogramPositionId) < 0
    ) {
      const valueIndex = hiddenOptionsAndValues.monogramPositions.indexOf(hiddenMonogramPositionId);
      if (valueIndex >= 0) {
        logConsole(
          'checkHiddenOptionsValuesToRemoveOptionItem for monogram position',
          { valueIndex, hiddenMonogramPositionId, hiddenOptionsAndValues },
          isLogEnabled,
        );
        hiddenOptionsAndValues.monogramPositions.splice(valueIndex, 1);
      }
    }
  });
};

const unhidePreviouslyHiddenOptionsAndValuesByOptionSelection = (
  previosSelectionRules: OptionsBusinessRulesItem,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  tmpCheckHiddenOptionsAndValues: HiddenOptionsAndValues,
  configurationType: ConfigurationTypes,
) => {
  if (previosSelectionRules.hide?.optionValues) {
    Object.entries(previosSelectionRules.hide?.optionValues).forEach(([applyToConfigurationType, optionValueIds]) => {
      optionValueIds.forEach(optionValueId => {
        if (
          !isOptionValueHiddenByRule(
            optionValueId,
            tmpCheckHiddenOptionsAndValues,
            applyToConfigurationType as ConfigurationTypes,
          )
        ) {
          const valueIndex =
            hiddenOptionsAndValues.optionValuesPerConfiguration[applyToConfigurationType as ConfigurationTypes].indexOf(
              optionValueId,
            );
          if (valueIndex >= 0) {
            hiddenOptionsAndValues.optionValuesPerConfiguration[applyToConfigurationType as ConfigurationTypes].splice(
              valueIndex,
              1,
            );
          }
        }
      });
    });
  }

  // Loop hide rules for optionValue selection on options if exists
  previosSelectionRules.hide?.options?.forEach((hiddenOptionId: number) => {
    if (!isOptionHiddenByRule(hiddenOptionId, tmpCheckHiddenOptionsAndValues, configurationType)) {
      const valueIndex = hiddenOptionsAndValues.optionsPerConfiguration[configurationType].indexOf(hiddenOptionId);
      if (valueIndex >= 0) {
        hiddenOptionsAndValues.optionsPerConfiguration[configurationType].splice(valueIndex, 1);
      }
    }
  });
};

// Need to be applied to all configuration types, loop through all configuration types when calling this fn
const unhidePreviouslyHiddenOptionsAndValuesByFabricSelection = (
  previousFabricSelectionRules: FabricBusinessRulesItem,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  tmpCheckHiddenOptionsAndValues: HiddenOptionsAndValues,
  configurationType: ConfigurationTypes,
  isLogEnabled: boolean,
) => {
  // Loop hide rules for optionValue selection on optionValue if exists
  previousFabricSelectionRules.hide?.optionValues?.forEach((hiddenOptionValueId: number) => {
    if (isOptionValueHiddenByRule(hiddenOptionValueId, tmpCheckHiddenOptionsAndValues, configurationType)) {
      const valueIndex =
        hiddenOptionsAndValues.optionValuesPerConfiguration[configurationType].indexOf(hiddenOptionValueId);
      if (valueIndex >= 0) {
        logConsole(
          'checkHiddenOptionsValuesToRemoveOptionItem for optionValue',
          { valueIndex, hiddenOptionValueId, hiddenOptionsAndValues },
          isLogEnabled,
        );
        hiddenOptionsAndValues.optionValuesPerConfiguration[configurationType].splice(valueIndex, 1);
      }
    }
  });

  // Loop hide rules for optionValue selection on options if exists
  previousFabricSelectionRules.hide?.options?.forEach((hiddenOptionId: number) => {
    if (isOptionHiddenByRule(hiddenOptionId, tmpCheckHiddenOptionsAndValues, configurationType)) {
      const valueIndex = hiddenOptionsAndValues.optionsPerConfiguration[configurationType].indexOf(hiddenOptionId);
      if (valueIndex >= 0) {
        logConsole(
          'checkHiddenOptionsValuesToRemoveOptionItem for option',
          { valueIndex, hiddenOptionId, hiddenOptionsAndValues },
          isLogEnabled,
        );
        hiddenOptionsAndValues.optionsPerConfiguration[configurationType].splice(valueIndex, 1);
      }
    }
  });
};

const checkOptionValueToFill = (
  selectedOptions: OptionsSelection,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  option: Option,
  defaultValues: DefaultValues,
  configurationType: ConfigurationTypes,
  isLogEnabled: boolean,
) => {
  let isFound = false;
  // To handle extra configurations which have different configuration type id but using definitions from base product
  const baseConfigurationId = getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationType);
  const defaultValue = defaultValues.configurationTypes[baseConfigurationId].defaultOptionValues[option.id];

  if (!isOptionValueHiddenByRule(defaultValue, hiddenOptionsAndValues, configurationType)) {
    logConsole(
      'checkOptionValueToFill by default value',
      { defaultValue, selectedOptions, hiddenOptionsAndValues, option, defaultValues, configurationType },
      isLogEnabled,
    );
    selectedOptions[configurationType][option.id] = defaultValue;
    isFound = true;
  }

  !isFound &&
    option.optionValues?.forEach(optionValueId => {
      if (!isFound && !isOptionValueHiddenByRule(optionValueId, hiddenOptionsAndValues, configurationType)) {
        logConsole(
          'checkOptionValueToFill by first vitem',
          { optionValueId, selectedOptions, hiddenOptionsAndValues, option, defaultValues, configurationType },
          isLogEnabled,
        );
        selectedOptions[configurationType][option.id] = optionValueId;
        isFound = true;
      }
    });
};

const checkHiddenOptionsForEmptySelection = (
  selectedOptions: OptionsSelection,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  options: Option[],
  defaultValues: DefaultValues,
  configurationType: ConfigurationTypes,
  isLogEnabled: boolean,
) => {
  if (
    !hiddenOptionsAndValues.optionsPerConfiguration[configurationType] ||
    hiddenOptionsAndValues.optionsPerConfiguration[configurationType].length === 0
  ) {
    return;
  }

  options.forEach(option => {
    // Option group or option
    const relevantOptions = option.options || [option];

    relevantOptions.forEach(option => {
      if (
        isOptionHiddenByRule(option.id, hiddenOptionsAndValues, configurationType) &&
        !selectedOptions[configurationType][option.id] &&
        option.optionValues?.length
      ) {
        checkOptionValueToFill(
          selectedOptions,
          hiddenOptionsAndValues,
          option,
          defaultValues,
          configurationType,
          isLogEnabled,
        );
      }
    });
  });
};

const removePreviousHiddenOptionsAndValuesByFabric = (
  prevFabricId: number | undefined,
  businessRules: BusinessRules,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  configurationType: ConfigurationTypes,
  isLogEnabled: boolean,
) => {
  if (prevFabricId) {
    const previouslySelectedFabricRules = businessRules.fabrics[prevFabricId];

    if (previouslySelectedFabricRules) {
      unhidePreviouslyHiddenOptionsAndValuesByFabricSelection(
        previouslySelectedFabricRules,
        hiddenOptionsAndValues,
        structuredClone(hiddenOptionsAndValues),
        configurationType,
        isLogEnabled,
      );
      unhidePreviouslyHiddenMonogramValuesBySelection(
        previouslySelectedFabricRules,
        hiddenOptionsAndValues,
        isLogEnabled,
      );
    }
  }
};

const removePreviousHiddenOptionsAndValuesByOption = (
  prevValueId: number | undefined,
  selectedOptions: OptionsSelection,
  selectedFabricId: number,
  businessRules: BusinessRules,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  configurationType: ConfigurationTypes,
  product: ProductType,
  isLogEnabled: boolean,
) => {
  if (!prevValueId || !hiddenOptionsAndValues) return;
  const prevOptionValueAssociatedRules = businessRules.configurationTypes[configurationType]?.optionValues[prevValueId];
  let tmpHiddenOptionsAndValues = getProductDefaultHiddenItemsState(product);

  Object.keys(selectedOptions[configurationType]).forEach(selectedOptionId => {
    const optionValueId = selectedOptions[configurationType][+selectedOptionId];
    if (optionValueId !== prevValueId) {
      const selectedOptionValueAssociatedRules =
        businessRules.configurationTypes[configurationType]?.optionValues[optionValueId];
      if (selectedOptionValueAssociatedRules) {
        triggerOptionSelectionBasedHideRules(
          selectedOptionValueAssociatedRules,
          tmpHiddenOptionsAndValues,
          configurationType,
          isLogEnabled,
        );
      }
    }
  });

  // This is commented out because it might not be necessary to apply fabric rules here
  // Keeping it in case any issue regarding business rules pops up so that we have proper reference
  // Initially this is added because of specific edge case flow in Tuxedo which doesn't exist anymore
  // const fabricAssociatedRules = businessRules.fabrics[selectedFabricId];
  // if (fabricAssociatedRules) {
  //   Products[product].forEach(configurationTypeValue => {
  //     triggerFabricSelectionBasedHideRules(
  //       fabricAssociatedRules,
  //       tmpHiddenOptionsAndValues,
  //       configurationTypeValue,
  //       isLogEnabled,
  //     );
  //   });
  // }

  if (prevOptionValueAssociatedRules) {
    unhidePreviouslyHiddenOptionsAndValuesByOptionSelection(
      prevOptionValueAssociatedRules,
      hiddenOptionsAndValues,
      tmpHiddenOptionsAndValues,
      configurationType,
    );
    unhidePreviouslyHiddenMonogramValuesBySelection(
      prevOptionValueAssociatedRules,
      hiddenOptionsAndValues,
      isLogEnabled,
      tmpHiddenOptionsAndValues,
    );
  }
};

const checkOptionToOverride = (
  option: Option,
  selectedOptionId: number,
  isFound: boolean,
  selectedOptions: OptionsSelection,
  selectedFabricId: number,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  businessRules: BusinessRules,
  optionValues: typeOptionValue,
  configurationType: ConfigurationTypes,
  product: ProductType,
  isLogEnabled: boolean,
) => {
  if (option.id === selectedOptionId) {
    option.optionValues?.forEach(optionValueId => {
      if (!isOptionValueHiddenByRule(optionValueId, hiddenOptionsAndValues, configurationType) && !isFound) {
        logConsole(
          'checkOptionToOverride',
          {
            optionValueId,
            selectedOptions,
            selectedFabricId,
            hiddenOptionsAndValues,
            businessRules,
            optionValues,
            configurationType,
          },
          isLogEnabled,
        );
        const prevValueId = selectedOptions[configurationType][+selectedOptionId];
        removePreviousHiddenOptionsAndValuesByOption(
          prevValueId,
          selectedOptions,
          selectedFabricId,
          businessRules,
          hiddenOptionsAndValues,
          configurationType,
          product,
          isLogEnabled,
        );
        selectedOptions[configurationType][+selectedOptionId] = optionValueId;
        isFound = true;

        businessRulesForOptionSelection(
          selectedOptions,
          selectedFabricId,
          businessRules,
          hiddenOptionsAndValues,
          optionValues,
          optionValueId,
          configurationType,
          product,
          isLogEnabled,
        );
      }
    });
  }
};

const checkEmptyOption = (
  selectedOptions: OptionsSelection,
  selectedFabricId: number,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  options: Option[],
  businessRules: BusinessRules,
  optionValues: typeOptionValue,
  configurationType: ConfigurationTypes,
  product: ProductType,
  isLogEnabled: boolean,
) => {
  Object.keys(selectedOptions[configurationType]).forEach(selectedOptionId => {
    if (
      isOptionValueHiddenByRule(
        selectedOptions[configurationType][+selectedOptionId],
        hiddenOptionsAndValues,
        configurationType,
      )
    ) {
      let isFound = false;
      options.forEach(option => {
        if (option.options && !isFound) {
          option.options.forEach(subOption => {
            if (!isFound) {
              checkOptionToOverride(
                subOption,
                +selectedOptionId,
                isFound,
                selectedOptions,
                selectedFabricId,
                hiddenOptionsAndValues,
                businessRules,
                optionValues,
                configurationType,
                product,
                isLogEnabled,
              );
            }
          });
        } else if (!isFound) {
          checkOptionToOverride(
            option,
            +selectedOptionId,
            isFound,
            selectedOptions,
            selectedFabricId,
            hiddenOptionsAndValues,
            businessRules,
            optionValues,
            configurationType,
            product,
            isLogEnabled,
          );
        }
      });
    }
  });
};

const businessRulesForOptionSelection = (
  selectedOptions: OptionsSelection,
  selectedFabricId: number,
  businessRules: BusinessRules,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  optionValues: typeOptionValue,
  selectedOptionValueId: number,
  configurationType: ConfigurationTypes,
  product: ProductType,
  isLogEnabled: boolean,
  prevValueId?: number,
) => {
  if (businessRules.configurationTypes[configurationType]) {
    removePreviousHiddenOptionsAndValuesByOption(
      prevValueId,
      selectedOptions,
      selectedFabricId,
      businessRules,
      hiddenOptionsAndValues,
      configurationType,
      product,
      isLogEnabled,
    );
    const businessRulesForSelectedOptionValue =
      businessRules.configurationTypes[configurationType]?.optionValues[selectedOptionValueId];

    if (businessRulesForSelectedOptionValue) {
      if (businessRulesForSelectedOptionValue.select?.optionValues) {
        Object.entries(businessRulesForSelectedOptionValue.select.optionValues).forEach(
          ([applyToConfigurationType, optionValueIds]) => {
            optionValueIds.forEach(optionValueId => {
              if (
                optionValues[optionValueId] &&
                selectedOptions[+applyToConfigurationType][optionValues[optionValueId].optionId] !== optionValueId
              ) {
                removePreviousHiddenOptionsAndValuesByOption(
                  selectedOptions[+applyToConfigurationType][optionValues[optionValueId].optionId],
                  selectedOptions,
                  selectedFabricId,
                  businessRules,
                  hiddenOptionsAndValues,
                  applyToConfigurationType as ConfigurationTypes,
                  product,
                  isLogEnabled,
                );
                selectedOptions[+applyToConfigurationType][optionValues[optionValueId].optionId] = optionValueId;

                businessRulesForOptionSelection(
                  selectedOptions,
                  selectedFabricId,
                  businessRules,
                  hiddenOptionsAndValues,
                  optionValues,
                  optionValueId,
                  applyToConfigurationType as ConfigurationTypes,
                  product,
                  isLogEnabled,
                );
              }
            });
          },
        );
      }
      // Loop select rules for option selection on optionvalues if exists
      triggerOptionSelectionBasedHideRules(
        businessRulesForSelectedOptionValue,
        hiddenOptionsAndValues,
        configurationType,
        isLogEnabled,
      );
    }
  }
};

const applyDefaultValues = (
  selectedOptions: OptionsSelection,
  selectedFabricId: number,
  defaultValues: DefaultValues,
  businessRules: BusinessRules,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  optionValues: typeOptionValue,
  product: ProductType,
  isLogEnabled: boolean,
) => {
  Products[product].forEach(configurationTypeValue => {
    // Have to check if it is an extra configuration, then we need to get the matching configuration type to read proper definition for default values
    const isExtraConfigurationType = isExtraConfigurationInProduct(product, configurationTypeValue);
    const selectionConfigurationTypeId = configurationTypeValue;
    // To handle extra configurations which have different configuration type id but using definitions from base product
    const baseDefinitionConfigurationTypeId = isExtraConfigurationType
      ? getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeValue)
      : configurationTypeValue;
    if (defaultValues.configurationTypes[baseDefinitionConfigurationTypeId]) {
      Object.keys(defaultValues.configurationTypes[baseDefinitionConfigurationTypeId].defaultOptionValues).forEach(
        (key: string) => {
          const optionId = parseInt(key);
          const optionValueId =
            defaultValues.configurationTypes[baseDefinitionConfigurationTypeId].defaultOptionValues[key];

          if (!selectedOptions[selectionConfigurationTypeId][optionId]) {
            logConsole(
              'applyDefaultValues',
              {
                optionValueId,
                selectedOptions,
                selectedFabricId,
                hiddenOptionsAndValues,
                businessRules,
                optionValues,
                defaultValues,
              },
              isLogEnabled,
            );
            selectedOptions[selectionConfigurationTypeId][optionId] = optionValueId;
            // Loop for each optionValue rule if necessary
            businessRulesForOptionSelection(
              selectedOptions,
              selectedFabricId,
              businessRules,
              hiddenOptionsAndValues,
              optionValues,
              optionValueId,
              selectionConfigurationTypeId, // this should be product configuratoin type
              product,
              isLogEnabled,
            );
          }
        },
      );
    }
  });
};

const applyFirstPhaseBusinessRulesForFabricSelection = (
  selectedOptions: OptionsSelection,
  businessRules: BusinessRules,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  optionValues: typeOptionValue,
  selectedFabricId: number,
  options: OptionConfigurationType,
  defaultValues: DefaultValues,
  product: ProductType,
  selectedBaseStyle: Record<string, StylePreset>,
  isLogEnabled: boolean,
) => {
  logConsole(
    'Entry applyFirstPhaseBusinessRulesForFabricSelection',
    {
      selectedOptions,
      businessRules,
      hiddenOptionsAndValues,
      optionValues,
      selectedFabricId,
      options,
      defaultValues,
      product,
      selectedBaseStyle,
    },
    isLogEnabled,
  );
  // Loop select rules for fabric selection from scratch
  if (businessRules.fabrics[selectedFabricId]) {
    businessRules.fabrics[selectedFabricId].select?.optionValues?.forEach(optionValueId => {
      const optionValue = optionValues[optionValueId];

      if (optionValue) {
        const baseConfigurationType = optionValue?.configurationTypeId?.toString() as ConfigurationTypes;
        const matchingExtraConfigurationType = getExtraConfgurationByBaseConfiguration(product, baseConfigurationType);

        selectedOptions[baseConfigurationType] = {
          ...selectedOptions[baseConfigurationType],
          [optionValues[optionValueId]?.optionId]: optionValueId,
        };

        businessRulesForOptionSelection(
          selectedOptions,
          selectedFabricId,
          businessRules,
          hiddenOptionsAndValues,
          optionValues,
          optionValueId,
          baseConfigurationType,
          product,
          isLogEnabled,
        );

        // If there is extra configuration for specific configuration type, apply rules to that as well
        if (matchingExtraConfigurationType) {
          selectedOptions[matchingExtraConfigurationType] = {
            ...selectedOptions[matchingExtraConfigurationType],
            [optionValues[optionValueId]?.optionId]: optionValueId,
          };

          businessRulesForOptionSelection(
            selectedOptions,
            selectedFabricId,
            businessRules,
            hiddenOptionsAndValues,
            optionValues,
            optionValueId,
            matchingExtraConfigurationType,
            product,
            isLogEnabled,
          );
        }
      }
    });

    Products[product].forEach(configurationTypeValue => {
      triggerFabricSelectionBasedHideRules(
        businessRules.fabrics[selectedFabricId],
        hiddenOptionsAndValues,
        configurationTypeValue,
        isLogEnabled,
      );
    });
  }

  Products[product].forEach(configurationTypeValue => {
    // Have to check if it is an extra configuration, then we need to get the matching configuration type
    const isExtraConfigurationType = isExtraConfigurationInProduct(product, configurationTypeValue);
    const selectionConfigurationTypeId = configurationTypeValue;
    // To handle extra configurations which have different configuration type id but using definitions from base product
    const baseDefinitionConfigurationTypeId = isExtraConfigurationType
      ? getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeValue)
      : configurationTypeValue;

    if (options[baseDefinitionConfigurationTypeId] && selectedBaseStyle[selectionConfigurationTypeId]) {
      applyBusinessRulesForStylePresetSelection(
        selectedBaseStyle[selectionConfigurationTypeId],
        selectedOptions,
        selectedFabricId,
        businessRules,
        hiddenOptionsAndValues,
        optionValues,
        configurationTypeValue,
        options[baseDefinitionConfigurationTypeId].items,
        product,
        false, // checkEmptyOptions
        isLogEnabled,
      );
    }
  });

  // Loop default values
  applyDefaultValues(
    selectedOptions,
    selectedFabricId,
    defaultValues,
    businessRules,
    hiddenOptionsAndValues,
    optionValues,
    product,
    isLogEnabled,
  );
};

const applySecondPhaseBusinessRulesForFabricSelection = (
  businessRules: BusinessRules,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  selectedFabricId: number,
  product: ProductType,
  isLogEnabled: boolean,
  prevFabricId?: number,
) => {
  logConsole(
    'Entry applySecondPhaseBusinessRulesForFabricSelection',
    { businessRules, hiddenOptionsAndValues, selectedFabricId, product, prevFabricId },
    isLogEnabled,
  );

  Products[product].forEach(configurationType => {
    if (prevFabricId && businessRules.configurationTypes[configurationType]) {
      removePreviousHiddenOptionsAndValuesByFabric(
        prevFabricId,
        businessRules,
        hiddenOptionsAndValues,
        configurationType,
        isLogEnabled,
      );
    }

    if (businessRules.fabrics[selectedFabricId]) {
      triggerFabricSelectionBasedHideRules(
        businessRules.fabrics[selectedFabricId],
        hiddenOptionsAndValues,
        configurationType,
        isLogEnabled,
      );
    }
  });
};

const applyOptionHideBusinessRulesBasedOnSelectedValues = (
  businessRules: BusinessRules,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  product: ProductType,
  selectedOptions: OptionsSelection,
  isLogEnabled: boolean,
) => {
  for (const configurationType of Products[product]) {
    if (!selectedOptions[configurationType]) continue;

    Object.values(selectedOptions[configurationType]).forEach(selectedOptionValue => {
      if (businessRules.configurationTypes[configurationType]?.optionValues[selectedOptionValue]) {
        triggerOptionSelectionBasedHideRules(
          businessRules.configurationTypes[configurationType]?.optionValues[selectedOptionValue],
          hiddenOptionsAndValues,
          configurationType,
          isLogEnabled,
        );
      }
    });
  }
};

export const applyBusinessRulesForFabricSelection = (
  paramSelectedOptions: OptionsSelection,
  businessRules: BusinessRules,
  paramHiddenOptionsAndValues: HiddenOptionsAndValues,
  optionValues: typeOptionValue,
  selectedFabricId: number,
  options: OptionConfigurationType,
  defaultValues: DefaultValues,
  product: ProductType,
  isManualSelectionDone: boolean,
  selectedBaseStyle: Record<string, StylePreset>,
  isLogEnabled: boolean,
  prevFabricId?: number,
  isPreloadFlow?: boolean,
  isPreloadSelectionApplied?: boolean,
) => {
  logConsole(
    'Entry applyBusinessRulesForFabricSelection',
    {
      paramSelectedOptions,
      businessRules,
      paramHiddenOptionsAndValues,
      optionValues,
      selectedFabricId,
      options,
      defaultValues,
      product,
      isManualSelectionDone,
      selectedBaseStyle,
      prevFabricId,
      isPreloadFlow,
      isPreloadSelectionApplied,
    },
    isLogEnabled,
  );
  let selectedOptions: OptionsSelection = initialiseConfigurationBasedOptionsSelection(product);
  let hiddenOptionsAndValues = getProductDefaultHiddenItemsState(product);
  if (isManualSelectionDone || isPreloadFlow) {
    selectedOptions = { ...paramSelectedOptions };
    hiddenOptionsAndValues = { ...paramHiddenOptionsAndValues };

    // This should be triggered only on initial load of preload flow
    isPreloadFlow &&
      !isPreloadSelectionApplied &&
      applyOptionHideBusinessRulesBasedOnSelectedValues(
        businessRules,
        hiddenOptionsAndValues,
        product,
        selectedOptions,
        isLogEnabled,
      );

    applySecondPhaseBusinessRulesForFabricSelection(
      businessRules,
      hiddenOptionsAndValues,
      selectedFabricId,
      product,
      isLogEnabled,
      prevFabricId,
    );
  } else {
    applyFirstPhaseBusinessRulesForFabricSelection(
      selectedOptions,
      businessRules,
      hiddenOptionsAndValues,
      optionValues,
      selectedFabricId,
      options,
      defaultValues,
      product,
      selectedBaseStyle,
      isLogEnabled,
    );
  }

  // Check all configurationtypes empty options
  Products[product].forEach(configurationTypeValue => {
    if (options[configurationTypeValue]) {
      checkEmptyOption(
        selectedOptions,
        selectedFabricId,
        hiddenOptionsAndValues,
        options[configurationTypeValue].items,
        businessRules,
        optionValues,
        configurationTypeValue,
        product,
        isLogEnabled,
      );
    }
  });

  return { selectedOptions, hiddenOptionsAndValues };
};

export const applyBusinessRulesForOptionSelection = (
  selectedOptions: OptionsSelection,
  selectedFabricId: number,
  businessRules: BusinessRules,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  optionValues: typeOptionValue,
  defaultValues: DefaultValues,
  selectedOptionValueId: number,
  configurationType: ConfigurationTypes,
  options: Option[],
  product: ProductType,
  isLogEnabled: boolean,
  prevValueId?: number,
) => {
  logConsole(
    'Entry applyBusinessRulesForOptionSelection',
    {
      selectedOptions,
      selectedFabricId,
      businessRules,
      hiddenOptionsAndValues,
      optionValues,
      defaultValues,
      selectedOptionValueId,
      configurationType,
      options,
      product,
      prevValueId,
    },
    isLogEnabled,
  );
  checkHiddenOptionsForEmptySelection(
    selectedOptions,
    hiddenOptionsAndValues,
    options,
    defaultValues,
    configurationType,
    isLogEnabled,
  );

  businessRulesForOptionSelection(
    selectedOptions,
    selectedFabricId,
    businessRules,
    hiddenOptionsAndValues,
    optionValues,
    selectedOptionValueId,
    configurationType,
    product,
    isLogEnabled,
    prevValueId,
  );

  checkEmptyOption(
    selectedOptions,
    selectedFabricId,
    hiddenOptionsAndValues,
    options,
    businessRules,
    optionValues,
    configurationType,
    product,
    isLogEnabled,
  );
};

export const applyBusinessRulesForStylePresetSelection = (
  style: StylePreset,
  selectedOptions: OptionsSelection,
  selectedFabricId: number,
  businessRules: BusinessRules,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  optionValues: typeOptionValue,
  configurationType: ConfigurationTypes,
  options: Option[],
  product: ProductType,
  checkEmptyOptions: boolean,
  isLogEnabled: boolean,
) => {
  logConsole(
    'Entry applyBusinessRulesForStylePresetSelection',
    {
      style,
      selectedOptions,
      selectedFabricId,
      businessRules,
      hiddenOptionsAndValues,
      optionValues,
      configurationType,
      options,
      product,
      checkEmptyOptions,
    },
    isLogEnabled,
  );
  style.presetOptions.forEach(presetOption => {
    if (optionValues[presetOption.optionValueId] && selectedOptions[configurationType]) {
      const prevValueId = selectedOptions[configurationType][presetOption.optionId];
      selectedOptions[configurationType][presetOption.optionId] = presetOption.optionValueId;

      businessRulesForOptionSelection(
        selectedOptions,
        selectedFabricId,
        businessRules,
        hiddenOptionsAndValues,
        optionValues,
        presetOption.optionValueId,
        configurationType,
        product,
        isLogEnabled,
        prevValueId,
      );
    }
  });

  if (checkEmptyOptions) {
    checkEmptyOption(
      selectedOptions,
      selectedFabricId,
      hiddenOptionsAndValues,
      options,
      businessRules,
      optionValues,
      configurationType,
      product,
      isLogEnabled,
    );
  }
};
